// Stub compatibile con "@base44/sdk" v5 - SOLUZIONE DEFINITIVA + CONTENT REMOTO
console.log('[RB] base44-sdk stub v5: loading — progress & remote content');

// ------------------------- util base ---------------------------------
function load(key, fallback) {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; }
  catch { return fallback; }
}
function save(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch {} }
function uid(prefix = 'id') { return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`; }
function nowISO() { return new Date().toISOString(); }

async function fetchJSON(url, opts = {}) {
  const res = await fetch(url, { headers: { accept: 'application/json', ...(opts.headers || {}) }, ...opts });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

// ------------------------- chiavi storage ----------------------------
const KEYS = {
  Lesson: 'RB_Lesson',
  DailyCheckIn: 'RB_DailyCheckIn',
  LessonFeedback: 'RB_LessonFeedback',
  UserProgress: 'RB_UserProgress',
  User: 'RB_UserProfile',
};

// ------------------------- utente ------------------------------------
function currentUser() { return load(KEYS.User, null); }

function ensureGuest() {
  let u = currentUser();
  if (!u) {
    u = {
      id: uid('user'),
      email: `guest_${Math.random().toString(36).slice(2,8)}@local`,
      full_name: 'Guest User',
      created_at: nowISO(),
      setup_completed: false,
    };
    save(KEYS.User, u);
  }
  return u;
}

// per-utente: tutto tranne Lesson
function scopeToUser(items, KEY) {
  if (KEY === KEYS.Lesson) return items;
  const u = ensureGuest();
  return (items || []).filter(it => it.created_by === u.email);
}

// ------------------------- progress helpers --------------------------
function normalizeProgress(data) {
  return {
    ...data,
    lesson_completed: !!(data.lesson_completed ?? false),
    exercise_completed: !!(data.exercise_completed ?? false),
  };
}

// Pulisce duplicati per la stessa lezione (per utente)
function cleanupUserProgress() {
  const u = ensureGuest();
  const all = load(KEYS.UserProgress, []);
  const mine = all.filter(x => x.created_by === u.email);
  const others = all.filter(x => x.created_by !== u.email);

  const byLesson = {};
  for (const r of mine) {
    const k = r.lesson_id || '__no_lesson__';
    (byLesson[k] ||= []).push(r);
  }

  const cleaned = [];
  for (const [lesson_id, recs] of Object.entries(byLesson)) {
    if (recs.length === 1) {
      cleaned.push(normalizeProgress(recs[0]));
      continue;
    }
    // merge coerente
    const base = recs[0];
    cleaned.push(normalizeProgress({
      id: base.id,
      created_by: u.email,
      created_at: base.created_at,
      updated_at: nowISO(),
      lesson_id: lesson_id === '__no_lesson__' ? null : lesson_id,
      lesson_completed: recs.some(r => r.lesson_completed),
      exercise_completed: recs.some(r => r.exercise_completed),
      exercise_answer: recs.find(r => r.exercise_answer)?.exercise_answer ?? null,
      completion_date: recs.find(r => r.completion_date)?.completion_date ?? null,
    }));
  }

  save(KEYS.UserProgress, [...others, ...cleaned]);
  console.log('[RB] cleanupUserProgress:', { before: mine.length, after: cleaned.length });
}
cleanupUserProgress();

// ------------------------- store generico ----------------------------
function createStore(KEY) {
  const isProgress = KEY === KEYS.UserProgress;

  return {
    async list() {
      const all = load(KEY, []);
      const scoped = scopeToUser(all, KEY);
      return isProgress ? scoped.map(normalizeProgress) : scoped;
    },

    async filter(where = {}) {
      const all = load(KEY, []);
      const scoped = scopeToUser(all, KEY);
      const out = scoped.filter(it => Object.entries(where).every(([k, v]) => it[k] === v));
      return isProgress ? out.map(normalizeProgress) : out;
    },

    async get(id) {
      const all = load(KEY, []);
      const r = all.find(x => x.id === id);
      return r ? (isProgress ? normalizeProgress(r) : r) : null;
    },

    async create(data) {
      const u = ensureGuest();
      const all = load(KEY, []);

      if (isProgress && data?.lesson_id) {
        // upsert per lesson_id + created_by
        const i = all.findIndex(p => p.lesson_id === data.lesson_id && p.created_by === u.email);
        if (i !== -1) {
          const merged = {
            ...all[i],
            ...data,
            lesson_completed: !!(data.lesson_completed || all[i].lesson_completed),
            exercise_completed: !!(data.exercise_completed || all[i].exercise_completed),
            updated_at: nowISO(),
          };
          all[i] = normalizeProgress(merged);
          save(KEY, all);
          return all[i];
        }
      }

      const rec = {
        id: uid(KEY.replace('RB_', '').toLowerCase()),
        created_by: u.email,
        created_at: nowISO(),
        updated_at: nowISO(),
        ...data,
      };
      all.push(rec);
      save(KEY, all);
      return isProgress ? normalizeProgress(rec) : rec;
    },

    async update(id, patch) {
      if (isProgress && patch?.lesson_id) {
        // stesso comportamento dell'upsert
        return this.create(patch);
      }
      const all = load(KEY, []);
      const i = all.findIndex(x => x.id === id);
      if (i < 0) throw new Error(`Record ${id} non trovato`);
      const next = { ...all[i], ...patch, updated_at: nowISO() };
      all[i] = isProgress ? normalizeProgress(next) : next;
      save(KEY, all);
      return all[i];
    },

    async delete(id) {
      const all = load(KEY, []);
      const next = all.filter(x => x.id !== id);
      if (next.length !== all.length) {
        save(KEY, next);
        return { success: true };
      }
      return { success: false };
    },
  };
}

// ------------------------- REMOTE LESSONS ----------------------------
// Lezioni: prova a leggere da /api/content (fonte centrale), altrimenti fallback locale.
async function listLessonsRemoteFirst() {
  try {
    const data = await fetchJSON('/api/content'); // deve rispondere { lessons: [...] }
    if (Array.isArray(data?.lessons) && data.lessons.length) {
      // normalizza ordine
      const sorted = [...data.lessons].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
      save(KEYS.Lesson, sorted); // cache locale per offline
      return sorted;
    }
    // se l'API risponde ma senza lessons, cadiamo al fallback
    console.warn('[RB] /api/content senza lessons valide — uso fallback localStorage');
  } catch (e) {
    console.warn('[RB] impossibile leggere /api/content — uso fallback localStorage', e.message);
  }
  const fallback = load(KEYS.Lesson, []);
  return [...fallback].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
}

const Lesson = {
  async list() { return listLessonsRemoteFirst(); },
  async filter(where = {}) {
    const ls = await listLessonsRemoteFirst();
    return ls.filter(it => Object.entries(where).every(([k, v]) => it[k] === v));
  },
  async get(idOrSlug) {
    const ls = await listLessonsRemoteFirst();
    return ls.find(l => l.id === idOrSlug || l.slug === idOrSlug) || null;
  },
  // Per sicurezza, manteniamo le API ma le usiamo solo come cache locale (dev/admin).
  async create(data) {
    const all = load(KEYS.Lesson, []);
    const rec = { id: uid('lesson'), created_at: nowISO(), ...data };
    all.push(rec);
    save(KEYS.Lesson, all);
    return rec;
  },
  async update(id, patch) {
    const all = load(KEYS.Lesson, []);
    const i = all.findIndex(x => x.id === id);
    if (i < 0) return null;
    all[i] = { ...all[i], ...patch, updated_at: nowISO() };
    save(KEYS.Lesson, all);
    return all[i];
  },
  async delete(id) {
    const all = load(KEYS.Lesson, []);
    const next = all.filter(x => x.id !== id);
    save(KEYS.Lesson, next);
    return { success: true };
  }
};

// ------------------------- seed minimo locale ------------------------
(function seedLessonsIfEmpty() {
  const hasLocal = Array.isArray(load(KEYS.Lesson, null)) && load(KEYS.Lesson, null)?.length > 0;
  if (!hasLocal) {
    const seed = [
      { id: 'l1', order: 1, title: 'Respirazione diaframmatica', slug: 'respirazione-diaframmatica', duration: 5, created_at: nowISO() },
      { id: 'l2', order: 2, title: 'Box breathing 4-4-4-4', slug: 'box-breathing', duration: 5, created_at: nowISO() },
    ];
    save(KEYS.Lesson, seed);
  }
})();

// ------------------------- export stores -----------------------------
export const UserProgress   = createStore(KEYS.UserProgress);
export const DailyCheckIn   = createStore(KEYS.DailyCheckIn);
export const LessonFeedback = createStore(KEYS.LessonFeedback);

export const User = {
  async me() { return ensureGuest(); },
  async updateMyUserData(data) {
    const u = ensureGuest();
    const next = { ...u, ...data, updated_at: nowISO() };
    save(KEYS.User, next);
    return next;
    },
  async logout() {
    localStorage.removeItem(KEYS.User);
    location.reload();
  },
  async login() { return ensureGuest(); },
  async loginWithRedirect(cb) { await this.login(); if (cb) location.href = cb; },
};

// mock integrazioni per compatibilità
export const googleDriveManager = async () => ({ success: true });

// default export (SDK compat)
const sdkDefault = { User, Lesson, UserProgress, DailyCheckIn, LessonFeedback };
export { Lesson };
export default sdkDefault;
